import { useState, useMemo, useCallback } from 'react';
import { CheckCircle, XCircle, Trash2, Search, Image } from 'lucide-react';
import {
  ROLES,
  COLLECTIONS_QUERIES,
  LISTING_ITEM_STATUSES,
  categoryMessages,
} from '../constants';
import { withAuth } from '../hoc/withAuth.tsx';
import { getQueryFilter, formatISODate } from '../utils';
import {
  useGetFirestoreItemsByQuery,
  useDeleteImages,
  useDeleteFirebaseItem,
  useUpdateFirestoreItem,
} from '../hooks';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { LayoutWrapper } from '../components/LayoutWrapper.tsx';
import { Select } from '../components/Select.tsx';

// Define types
type ServiceStatus = keyof typeof LISTING_ITEM_STATUSES;

const getStatusesSelectOptions = () => {
  return [{ value: 'all', label: 'All' }].concat(
    Object.keys(LISTING_ITEM_STATUSES).map((status) => ({
      value: status,
      label: status,
    })),
  );
};

// Skeleton Component
const TableSkeleton = () => (
  <tbody className='animate-pulse'>
    {[...Array(5)].map((_, index) => (
      <tr key={index} className='border-b'>
        <td className='px-6 py-4 whitespace-nowrap min-w-[200px]'>
          <div className='flex items-center'>
            <div className='h-10 w-10 bg-gray-200 rounded-lg' />
            <div className='ml-4 h-4 w-32 bg-gray-200 rounded' />
          </div>
        </td>
        <td className='px-6 py-4 whitespace-nowrap min-w-[150px]'>
          <div className='h-4 w-24 bg-gray-200 rounded' />
        </td>
        <td className='px-6 py-4 whitespace-nowrap min-w-[150px]'>
          <div className='h-4 w-20 bg-gray-200 rounded' />
        </td>
        <td className='px-6 py-4 whitespace-nowrap min-w-[100px]'>
          <div className='h-4 w-16 bg-gray-200 rounded' />
        </td>
        <td className='px-6 py-4 whitespace-nowrap min-w-[120px]'>
          <div className='h-4 w-20 bg-gray-200 rounded' />
        </td>
        <td className='px-6 py-4 whitespace-nowrap min-w-[150px]'>
          <div className='h-4 w-24 bg-gray-200 rounded' />
        </td>
        <td className='px-6 py-4 whitespace-nowrap min-w-[150px]'>
          <div className='h-4 w-24 bg-gray-200 rounded' />
        </td>
        <td className='px-6 py-4 whitespace-nowrap min-w-[120px]'>
          <div className='flex justify-end space-x-2'>
            <div className='h-5 w-5 bg-gray-200 rounded-full' />
            <div className='h-5 w-5 bg-gray-200 rounded-full' />
          </div>
        </td>
      </tr>
    ))}
  </tbody>
);

const AdminDashboardComponent = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ServiceStatus | 'all'>(
    'all',
  );
  const { mutate: deleteImages } = useDeleteImages();
  const { mutate: deleteServiceMutation } = useDeleteFirebaseItem();
  const { mutate: updateServiceItemStatus } = useUpdateFirestoreItem();

  const queryFilters = useMemo(() => {
    return statusFilter === 'all'
      ? []
      : [
          getQueryFilter({
            field: COLLECTIONS_QUERIES.status,
            value: statusFilter,
          }),
        ];
  }, [statusFilter]);

  const { formatMessage } = useIntl();
  const { data: allItems, isPending } =
    useGetFirestoreItemsByQuery(queryFilters);

  // Handlers
  const handleRowClick = useCallback(
    (serviceId: string) => {
      navigate(`/services/${serviceId}`);
    },
    [navigate],
  );

  const handleStatusChange = useCallback(
    (e: React.MouseEvent, serviceId: string, newStatus: ServiceStatus) => {
      e.stopPropagation(); // Prevent row click
      updateServiceItemStatus({
        id: serviceId,
        newData: {
          status: newStatus,
        },
        filters: queryFilters,
      });
    },
    [queryFilters, updateServiceItemStatus],
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent, service) => {
      e.stopPropagation(); // Prevent row click
      if (
        window.confirm(
          intl.formatMessage({
            id: 'areYouSureToRemoveItem',
            defaultMessage: 'Вы уверены, что хотите удалить эту услугу?',
          }),
        )
      ) {
        deleteImages(service.images);
        deleteServiceMutation({ id: service.id, filters: queryFilters });
      }
    },
    [intl, deleteImages, deleteServiceMutation, queryFilters],
  );

  // Utility functions
  const getStatusBadgeClass = (status: ServiceStatus) =>
    ({
      [LISTING_ITEM_STATUSES.awaitingApproval]: 'bg-yellow-100 text-yellow-800',
      [LISTING_ITEM_STATUSES.paused]: 'bg-blue-100 text-blue-800',
      [LISTING_ITEM_STATUSES.active]: 'bg-green-100 text-green-800',
      [LISTING_ITEM_STATUSES.rejected]: 'bg-red-100 text-red-800',
    }[status] || '');

  const getStatusText = (status: ServiceStatus) =>
    ({
      [LISTING_ITEM_STATUSES.awaitingApproval]: 'На рассмотрении',
      [LISTING_ITEM_STATUSES.active]: 'Одобрено',
      [LISTING_ITEM_STATUSES.rejected]: 'Отклонено',
      [LISTING_ITEM_STATUSES.paused]: 'Пауза',
    }[status]);

  // Render components
  const renderHeader = () => (
    <thead className='bg-gray-50 sticky top-0'>
      <tr>
        {[
          'Действия',
          'Статус',
          'Услуга',
          'Поставщик',
          'Категория',
          'Цена',
          'Дата создания',
          'Дата обновления',
        ].map((title, index) => (
          <th
            key={index}
            className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
            style={{
              minWidth:
                index === 0 || index === 1
                  ? '120px'
                  : index === 2
                  ? '200px'
                  : index === 6
                  ? '150px'
                  : index === 3 || index === 5
                  ? '100px'
                  : '120px',
              textAlign: index === 7 ? 'right' : 'left',
            }}
          >
            {title}
          </th>
        ))}
      </tr>
    </thead>
  );

  const renderServiceRow = (service: any) => (
    <tr
      key={service.id}
      className='hover:bg-gray-50 align-middle cursor-pointer'
      onClick={() => handleRowClick(service.id)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleRowClick(service.id);
        }
      }}
      aria-label={`View details for ${service.title}`}
    >
      <td
        className='px-6 py-4 whitespace-nowrap text-left text-sm font-medium'
        onClick={(e) => e.stopPropagation()} // Prevent row click when clicking this cell
      >
        <div className='flex space-x-2'>
          {![
            LISTING_ITEM_STATUSES.active,
            LISTING_ITEM_STATUSES.paused,
          ].includes(service.status) && (
            <button
              onClick={(e) =>
                handleStatusChange(e, service.id, LISTING_ITEM_STATUSES.active)
              }
              className='text-green-600 hover:text-green-900'
              title='Одобрить'
              aria-label='Approve service'
            >
              <CheckCircle className='w-5 h-5' />
            </button>
          )}
          {service.status !== LISTING_ITEM_STATUSES.rejected && (
            <button
              onClick={(e) =>
                handleStatusChange(
                  e,
                  service.id,
                  LISTING_ITEM_STATUSES.rejected,
                )
              }
              className='text-red-600 hover:text-red-900'
              title='Отклонить'
              aria-label='Reject service'
            >
              <XCircle className='w-5 h-5' />
            </button>
          )}
          <button
            onClick={(e) => handleDelete(e, service)}
            className='text-red-600 hover:text-red-900'
            title='Удалить'
            aria-label='Delete service'
          >
            <Trash2 className='w-5 h-5' />
          </button>
        </div>
      </td>
      <td className='px-6 py-4 whitespace-nowrap min-w-[120px]'>
        <span
          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
            service.status,
          )}`}
        >
          {getStatusText(service.status)}
        </span>
      </td>
      <td className='px-6 py-4 whitespace-nowrap min-w-[200px]'>
        <div className='flex items-center'>
          {service.images[0] ? (
            <img
              src={service.images[0]}
              alt={service.title}
              className='h-10 w-10 rounded-lg object-cover'
            />
          ) : (
            <Image className='w-10 h-10 text-gray-300' />
          )}
          <div className='ml-4 text-sm font-medium text-gray-900'>
            {service.title}
          </div>
        </div>
      </td>
      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 min-w-[150px]'>
        {service.providerUuid}
      </td>
      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 min-w-[150px]'>
        {formatMessage(categoryMessages[`category${service.category}`])}
      </td>
      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 min-w-[100px]'>
        {service.price}RON
      </td>
      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 min-w-[150px]'>
        {formatISODate(service.createdAt)}
      </td>
      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 min-w-[150px]'>
        {formatISODate(service?.updatedAt)}
      </td>
    </tr>
  );

  return (
    <LayoutWrapper>
      <main className='container mx-auto'>
        <section className='flex justify-between items-center mb-6'>
          <h1 className='text-2xl font-bold text-gray-900'>
            Управление услугами
          </h1>
          <div className='flex space-x-4'>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' />
              <input
                type='text'
                placeholder={formatMessage({
                  id: 'search.placeholder',
                  defaultMessage: 'Поиск услуг...',
                })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              />
            </div>

            <Select
              options={getStatusesSelectOptions()}
              value={statusFilter}
              onChange={setStatusFilter}
              className='no-padding min-w-[200px]'
            />
          </div>
        </section>

        <section className='bg-white rounded-lg shadow-sm overflow-x-auto'>
          <table className='w-full divide-y divide-gray-200'>
            {renderHeader()}
            {isPending ? (
              <TableSkeleton />
            ) : (
              <tbody className='bg-white divide-y divide-gray-200'>
                {allItems?.map(renderServiceRow)}
              </tbody>
            )}
          </table>
        </section>
      </main>
    </LayoutWrapper>
  );
};

export const AdminDashboard = withAuth(AdminDashboardComponent, ROLES.admin);
